import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { languages } from '../../lib/i18n';
import { AUTH_ENABLED } from '../../lib/featureFlags';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { 
  TreePine, 
  Calculator, 
  LayoutDashboard, 
  LineChart, 
  LogOut, 
  User,
  Globe,
  Menu,
  HelpCircle,
  Info,
  FolderOpen,
  FileText,
  Shield
} from 'lucide-react';

const Header = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { path: '/calculator', label: t('nav.calculator'), icon: Calculator },
    { path: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
    { path: '/sensitivity', label: t('nav.sensitivity'), icon: LineChart },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        {/* Logo */}
        <Link 
          to="/" 
          className="flex items-center gap-2 font-heading font-bold text-xl text-forest-700 hover:text-forest-600 transition-colors"
          data-testid="logo-link"
        >
          <TreePine className="h-6 w-6" />
          <span className="hidden sm:inline">Skogskalkyl</span>
        </Link>

        {/* Desktop Navigation - Always visible */}
        <nav className="hidden md:flex items-center gap-1">
          <Link
            to="/verktyg"
            data-testid="nav-verktyg"
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive('/verktyg')
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <Calculator className="h-4 w-4" />
            {language === 'de' ? 'Kalkulatoren' : language === 'en' ? 'Calculators' : language === 'se' ? 'Rehkenastit' : 'Kalkylatorer'}
          </Link>
          <a
            href="/#faq-section"
            data-testid="nav-faq"
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors text-muted-foreground hover:text-foreground hover:bg-muted"
          >
            <HelpCircle className="h-4 w-4" />
            {t('nav.faq')}
          </a>
          <Link
            to="/about"
            data-testid="nav-about"
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive('/about')
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <Info className="h-4 w-4" />
            {t('nav.about')}
          </Link>
          {isAuthenticated && navItems.filter(item => item.path !== '/calculator' && item.path !== '/verktyg').map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.path.slice(1)}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive(item.path)
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-2">
          {/* Language Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2" data-testid="language-selector">
                <Globe className="h-4 w-4" />
                <span className="hidden sm:inline">
                  {languages.find(l => l.code === language)?.name || 'Svenska'}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {languages.map((lang) => (
                <DropdownMenuItem
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className={language === lang.code ? 'bg-muted' : ''}
                  data-testid={`lang-${lang.code}`}
                >
                  <span className="mr-2">{lang.flag}</span>
                  {lang.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {isAuthenticated && AUTH_ENABLED ? (
            <>
              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu">
                    <User className="h-4 w-4" />
                    <span className="hidden sm:inline">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate('/mina-arenden')} data-testid="my-cases-btn">
                    <FolderOpen className="h-4 w-4 mr-2" />
                    Mina ärenden
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/mina-arenden?filter=pdf')} data-testid="my-beslutsbrev-btn">
                    <FileText className="h-4 w-4 mr-2" />
                    Mina Beslutsbrev
                  </DropdownMenuItem>
                  {user?.email?.toLowerCase() === 'info@hodema.se' && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-link">
                      <Shield className="h-4 w-4 mr-2" />
                      Admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                    <LogOut className="h-4 w-4 mr-2" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild className="md:hidden">
                  <Button variant="ghost" size="icon" data-testid="mobile-menu">
                    <Menu className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {navItems.map((item) => (
                    <DropdownMenuItem
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className={isActive(item.path) ? 'bg-muted' : ''}
                    >
                      <item.icon className="h-4 w-4 mr-2" />
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : AUTH_ENABLED ? (
            <div className="flex items-center gap-2">
              <Link to="/login">
                <Button variant="ghost" size="sm" data-testid="login-btn">
                  {t('nav.login')}
                </Button>
              </Link>
              <Link to="/register">
                <Button size="sm" data-testid="register-btn">
                  {t('nav.register')}
                </Button>
              </Link>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
};

export default Header;
